import { Modal } from "@hyperhedge/ui";
import {
  HyperliquidInfoClient,
  type HLAssetCtx,
  type HLAssetInfo,
} from "@hyperhedge/hyperliquid/client";
import { useQuery } from "@tanstack/react-query";
import type { CSSProperties } from "react";
import { useMemo, useState } from "react";
import type { SelectedPerp } from "../types";

const client = new HyperliquidInfoClient();

type Row = {
  name: string;
  maxLeverage: number;
  markPx: number;
  prevDayPx: number;
  dayNtlVlm: number;
  openInterest: number;
  funding: number;
};

function formatUsd(n: number): string {
  if (!isFinite(n) || n === 0) return "—";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(2)}`;
}

function formatPx(n: number): string {
  if (!isFinite(n)) return "—";
  if (n >= 1000) return n.toFixed(2);
  if (n >= 1) return n.toFixed(4);
  return n.toPrecision(4);
}

function toRows(meta: { universe: HLAssetInfo[] }, ctxs: HLAssetCtx[]): Row[] {
  const rows: Row[] = [];
  for (let i = 0; i < meta.universe.length; i++) {
    const info = meta.universe[i];
    const ctx = ctxs[i];
    if (!info || !ctx) continue;
    rows.push({
      name: info.name,
      maxLeverage: info.maxLeverage,
      markPx: Number(ctx.markPx),
      prevDayPx: Number(ctx.prevDayPx),
      dayNtlVlm: Number(ctx.dayNtlVlm),
      openInterest: Number(ctx.openInterest) * Number(ctx.markPx),
      funding: Number(ctx.funding),
    });
  }
  rows.sort((a, b) => b.dayNtlVlm - a.dayNtlVlm);
  return rows;
}

const HEADER_CELL: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 9,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--fg-meta)",
  padding: "8px 12px",
  textAlign: "right",
  borderBottom: "1px solid var(--line-2)",
  position: "sticky",
  top: 0,
  background: "var(--bg-1)",
  fontWeight: 500,
};

const CELL: CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 12,
  padding: "8px 12px",
  textAlign: "right",
  color: "var(--fg-hi)",
  whiteSpace: "nowrap",
};

function rowToSelected(r: Row): SelectedPerp {
  const dayChangePct =
    r.prevDayPx > 0 ? ((r.markPx - r.prevDayPx) / r.prevDayPx) * 100 : 0;
  return {
    coin: r.name,
    markPx: r.markPx,
    dayChangePct,
    dayNtlVlm: r.dayNtlVlm,
    openInterest: r.openInterest,
    fundingPct: r.funding * 100,
    maxLeverage: r.maxLeverage,
  };
}

export function HyperliquidMarketsModal({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (perp: SelectedPerp) => void;
}) {
  const [filter, setFilter] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["hl", "meta-and-asset-ctxs"],
    queryFn: () => client.getMetaAndAssetCtxs(),
    enabled: open,
    staleTime: 30_000,
  });

  const rows = useMemo(() => {
    if (!data) return [];
    const [meta, ctxs] = data;
    const all = toRows(meta, ctxs);
    if (!filter) return all;
    const q = filter.trim().toUpperCase();
    return all.filter((r) => r.name.toUpperCase().includes(q));
  }, [data, filter]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="HYPERLIQUID MARKETS"
      subtitle={data ? `${rows.length} OF ${data[0].universe.length} MARKETS` : "LOADING"}
      width="min(1200px, 95vw)"
      bodyStyle={{ padding: 0, display: "flex", flexDirection: "column" }}
    >
      <div
        style={{
          padding: "12px 22px",
          borderBottom: "1px solid var(--line-2)",
          display: "flex",
          gap: 12,
          alignItems: "center",
        }}
      >
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="SEARCH SYMBOL"
          style={{
            flex: 1,
            background: "var(--bg-0)",
            border: "1px solid var(--line-2)",
            color: "var(--fg-hi)",
            padding: "8px 12px",
            fontSize: 12,
            fontFamily: "var(--f-mono)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            outline: "none",
            borderRadius: 2,
          }}
        />
      </div>

      <div style={{ overflow: "auto", flex: 1, minHeight: 0 }}>
        {isLoading && (
          <div
            style={{
              padding: 48,
              textAlign: "center",
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--fg-meta)",
            }}
          >
            LOADING MARKETS…
          </div>
        )}
        {error && (
          <div
            style={{
              padding: 48,
              textAlign: "center",
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              color: "var(--sev-crit)",
            }}
          >
            FAILED TO LOAD — {String(error)}
          </div>
        )}
        {!isLoading && !error && (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <thead>
              <tr>
                <th style={{ ...HEADER_CELL, textAlign: "left" }}>SYMBOL</th>
                <th style={HEADER_CELL}>MARK</th>
                <th style={HEADER_CELL}>24H CHG</th>
                <th style={HEADER_CELL}>24H VOLUME</th>
                <th style={HEADER_CELL}>OPEN INTEREST</th>
                <th style={HEADER_CELL}>FUNDING (1H)</th>
                <th style={HEADER_CELL}>MAX LEV</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const chg = r.prevDayPx > 0 ? (r.markPx - r.prevDayPx) / r.prevDayPx : 0;
                const chgColor = chg >= 0 ? "var(--up)" : "var(--dn)";
                const fundingPct = r.funding * 100;
                const fundingColor = fundingPct >= 0 ? "var(--up)" : "var(--dn)";
                return (
                  <tr
                    key={r.name}
                    onClick={() => {
                      onSelect(rowToSelected(r));
                      onClose();
                    }}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--bg-2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <td
                      style={{
                        ...CELL,
                        textAlign: "left",
                        color: "var(--fg-max)",
                        fontWeight: 500,
                        borderBottom: "1px solid var(--line-1)",
                      }}
                    >
                      {r.name}
                    </td>
                    <td style={{ ...CELL, borderBottom: "1px solid var(--line-1)" }}>
                      {formatPx(r.markPx)}
                    </td>
                    <td
                      style={{
                        ...CELL,
                        color: chgColor,
                        borderBottom: "1px solid var(--line-1)",
                      }}
                    >
                      {chg >= 0 ? "+" : ""}
                      {(chg * 100).toFixed(2)}%
                    </td>
                    <td style={{ ...CELL, borderBottom: "1px solid var(--line-1)" }}>
                      {formatUsd(r.dayNtlVlm)}
                    </td>
                    <td style={{ ...CELL, borderBottom: "1px solid var(--line-1)" }}>
                      {formatUsd(r.openInterest)}
                    </td>
                    <td
                      style={{
                        ...CELL,
                        color: fundingColor,
                        borderBottom: "1px solid var(--line-1)",
                      }}
                    >
                      {fundingPct >= 0 ? "+" : ""}
                      {fundingPct.toFixed(4)}%
                    </td>
                    <td
                      style={{
                        ...CELL,
                        color: "var(--fg-meta)",
                        borderBottom: "1px solid var(--line-1)",
                      }}
                    >
                      {r.maxLeverage}×
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      ...CELL,
                      textAlign: "center",
                      color: "var(--fg-meta)",
                      padding: 32,
                    }}
                  >
                    NO MATCHES
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
